import { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { JOB_CATEGORIES, JobCategory } from '@/lib/types/job';

interface CategoryMultiSelectProps {
  selectedCategories: JobCategory[];
  onChange: (categories: JobCategory[]) => void;
}

export function CategoryMultiSelect({ selectedCategories, onChange }: CategoryMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggleCategory = (category: JobCategory) => {
    if (selectedCategories.includes(category)) {
      onChange(selectedCategories.filter(c => c !== category));
    } else {
      onChange([...selectedCategories, category]);
    }
  };

  const removeCategory = (category: JobCategory) => {
    onChange(selectedCategories.filter(c => c !== category));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="justify-between min-w-[180px]"
            >
              {selectedCategories.length === 0
                ? 'All Categories'
                : `${selectedCategories.length} selected`}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[220px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search categories..." />
              <CommandList>
                <CommandEmpty>No category found.</CommandEmpty>
                <CommandGroup>
                  {JOB_CATEGORIES.map((category) => (
                    <CommandItem
                      key={category.value}
                      value={category.value}
                      onSelect={() => toggleCategory(category.value)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedCategories.includes(category.value)
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {category.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedCategories.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="h-8 px-2 text-muted-foreground"
          >
            Clear all
          </Button>
        )}
      </div>

      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedCategories.map((category) => {
            const label = JOB_CATEGORIES.find(c => c.value === category)?.label || category;
            return (
              <Badge
                key={category}
                variant="secondary"
                className="gap-1 pr-1"
              >
                {label}
                <button
                  onClick={() => removeCategory(category)}
                  className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
